import { type OAuthError, useAuth0 } from '@auth0/auth0-react';
import { datadogRum } from '@datadog/browser-rum';
import { type ReactNode, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import { useLogin } from '#src/hooks/auth.tsx';
import { extractTokenField } from '#src/utils/token.ts';

import { useDatadog } from '../Datadog/Datadog.tsx';
import { Alert, Box, Stack, Text, TextLinkButton } from 'braid-design-system';

const NO_ORG_MEMBERSHIP_ERROR_DESCRIPTION =
  'client requires organization membership, but user does not belong to any organization';

export const AuthVerifier = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const { setIntegrationPartner, setUser } = useDatadog();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  const auth = useAuth0();
  const { login } = useLogin();

  const checkAuth = async () => {
    if (auth.isLoading || auth.isAuthenticated || pathname === '/authorize') {
      return;
    }
    return login({});
  };

  useEffect(() => {
    checkAuth();

    if (auth.isAuthenticated) {
      auth
        .getAccessTokenSilently()
        .then((token) => {
          if (!token) {
            return;
          }
          const field = extractTokenField(token, 'integrationPartnerUuid');
          const userId = auth.user?.sub;

          if (field) {
            datadogRum.setUser({ id: userId, integrationPartnerUuid: field });
            setIntegrationPartner(field);
          }

          if (userId) {
            setUser(userId);
          }
        })
        .catch((e: OAuthError) => {
          // Re-prompt for login if error is `login_required`
          if (e.error === 'login_required') {
            return login({});
          }
          // eslint-disable-next-line no-console
          console.error(e);
          throw e;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, auth.isLoading]);

  if (
    error === 'invalid_request' &&
    errorDescription === NO_ORG_MEMBERSHIP_ERROR_DESCRIPTION
  ) {
    return (
      <Box padding="gutter">
        <Alert tone="caution">
          <Stack space="small">
            <Text weight="strong">
              You are not associated with any integration partners.
            </Text>
            <Text>
              Please contact an administrator of your integration partner
              account to get access.
            </Text>
            <Text>
              <TextLinkButton
                onClick={() => {
                  auth.logout({
                    logoutParams: {
                      returnTo: window.location.origin,
                    },
                  });
                }}
              >
                Log out
              </TextLinkButton>
            </Text>
          </Stack>
        </Alert>
      </Box>
    );
  }

  return auth.isAuthenticated ? <>{children}</> : null;
};
